/**
 * Utility functions for Paper Trading page — trade mapping and metrics computation.
 */

import type { Trade, TradeAction, PerformanceMetrics, PaperTrade } from '../../types';
import { calculateEquityRiskMetrics, calculateSlippageUncertaintyBandPercent } from '../../../analysis/metrics';

/**
 * Map a PaperTrade action to a backtesting TradeAction.
 */
function mapAction(action: PaperTrade['action']): TradeAction {
  switch (action) {
    case 'open_long': return 'OPEN_LONG';
    case 'close_long': return 'CLOSE_LONG';
    case 'open_short': return 'OPEN_SHORT';
    case 'close_short': return 'CLOSE_SHORT';
  }
}

/**
 * Convert paper trades to backtesting Trade[] format for use with <Chart /> and <Dashboard />.
 */
export function mapPaperTrades(trades: PaperTrade[]): Trade[] {
  return trades.map((t) => ({
    id: String(t.id),
    symbol: t.symbol,
    action: mapAction(t.action),
    price: t.price,
    amount: t.amount,
    timestamp: t.timestamp,
    pnl: t.pnl ?? undefined,
    pnlPercent: t.pnlPercent ?? undefined,
    balanceAfter: t.balanceAfter,
    fee: t.fee,
    fundingIncome: t.fundingIncome ?? undefined,
  }));
}

/**
 * Compute PerformanceMetrics from paper trades and session state.
 *
 * @param trades          All paper trades for the session (or filtered by asset)
 * @param initialCapital  Starting capital of the session
 * @param currentEquity   Latest equity value (used for totalReturn calculation)
 * @param equitySnapshots Persisted equity snapshots — must carry `timestamp` for risk metrics
 * @param timeframe       Required timeframe string for Sharpe/Sortino annualization (e.g. '1h', '1d')
 */
export function computePaperMetrics(
  trades: PaperTrade[],
  initialCapital: number,
  currentEquity: number,
  equitySnapshots: { equity: number; timestamp: number }[],
  timeframe: string,
): PerformanceMetrics {
  const closeTrades = trades.filter(
    (t) => t.action === 'close_long' || t.action === 'close_short',
  );
  const wins = closeTrades.filter((t) => (t.pnl ?? 0) > 0);
  const losses = closeTrades.filter((t) => (t.pnl ?? 0) <= 0);
  const totalPnl = closeTrades.reduce((s, t) => s + (t.pnl ?? 0), 0);
  const totalFees = trades.reduce((s, t) => s + (t.fee ?? 0), 0);
  const totalWins = wins.reduce((s, t) => s + (t.pnl ?? 0), 0);
  const totalLosses = Math.abs(losses.reduce((s, t) => s + (t.pnl ?? 0), 0));
  const totalFunding = trades.reduce((s, t) => s + (t.fundingIncome ?? 0), 0);

  const avgWin = wins.length > 0 ? totalWins / wins.length : 0;
  const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + (t.pnl ?? 0), 0) / losses.length : 0;
  const avgWinPct = wins.length > 0 ? wins.reduce((s, t) => s + (t.pnlPercent ?? 0), 0) / wins.length : 0;
  const avgLossPct = losses.length > 0 ? losses.reduce((s, t) => s + (t.pnlPercent ?? 0), 0) / losses.length : 0;

  const totalReturn = currentEquity - initialCapital;
  const totalReturnPercent = initialCapital > 0 ? (totalReturn / initialCapital) * 100 : 0;
  const winRate = closeTrades.length > 0 ? (wins.length / closeTrades.length) * 100 : 0;
  const profitFactor = totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? Infinity : 0;
  const expectancy = closeTrades.length > 0 ? totalPnl / closeTrades.length : 0;
  const expectancyPct = closeTrades.length > 0
    ? closeTrades.reduce((s, t) => s + (t.pnlPercent ?? 0), 0) / closeTrades.length
    : 0;

  // Build EquityPoint[] from snapshots for risk metric calculation.
  // drawdown field is unused by calculateEquityRiskMetrics (it recomputes from equity values),
  // so we set it to 0 here.
  const equityPoints = equitySnapshots.map((s) => ({
    timestamp: s.timestamp,
    equity: s.equity,
    drawdown: 0,
  }));
  const {
    maxDrawdown,
    maxDrawdownPercent,
    sharpeRatio,
    sortinoRatio,
    cagrPercent,
    calmarRatio,
  } = calculateEquityRiskMetrics(equityPoints, initialCapital, timeframe);

  // Long/Short PnL breakdown
  const longCloseTrades = closeTrades.filter(t => t.action === 'close_long');
  const shortCloseTrades = closeTrades.filter(t => t.action === 'close_short');

  const longPnl = longCloseTrades.reduce((s, t) => s + (t.pnl ?? 0), 0);
  const shortPnl = shortCloseTrades.reduce((s, t) => s + (t.pnl ?? 0), 0);

  const longWins = longCloseTrades.filter(t => (t.pnl ?? 0) > 0).length;
  const shortWins = shortCloseTrades.filter(t => (t.pnl ?? 0) > 0).length;

  const longWinRate = longCloseTrades.length > 0 ? (longWins / longCloseTrades.length) * 100 : 0;
  const shortWinRate = shortCloseTrades.length > 0 ? (shortWins / shortCloseTrades.length) * 100 : 0;

  // SHA-28: +- intrabar-fill-uncertainty band (reporting metadata only,
  // parity with the backtest/GS/WF path's calculateMetrics()). Computed
  // from ALL trades (not just closes) — every fill is an actual exchange
  // (or simulated) fill event.
  const slippageUncertaintyBandPercent = calculateSlippageUncertaintyBandPercent(
    trades.map((t) => t.price * t.amount),
    initialCapital,
  );

  return {
    totalReturn,
    totalReturnPercent,
    maxDrawdown,
    maxDrawdownPercent,
    sharpeRatio,
    sortinoRatio,
    calmarRatio,
    cagrPercent,
    winRate,
    profitFactor,
    totalTrades: closeTrades.length,
    winningTrades: wins.length,
    losingTrades: losses.length,
    avgWin,
    avgLoss,
    avgWinPercent: avgWinPct,
    avgLossPercent: avgLossPct,
    expectancy,
    expectancyPercent: expectancyPct,
    largestWin: wins.length > 0 ? Math.max(...wins.map((t) => t.pnl ?? 0)) : 0,
    largestLoss: losses.length > 0 ? Math.min(...losses.map((t) => t.pnl ?? 0)) : 0,
    avgTradeDuration: 0,
    exposureTime: 0,
    totalFees,
    slippageUncertaintyBandPercent,
    longPnl,
    shortPnl,
    longTrades: longCloseTrades.length,
    shortTrades: shortCloseTrades.length,
    longWinRate,
    shortWinRate,
    ...(totalFunding !== 0 ? { totalFundingIncome: totalFunding, tradingPnl: totalPnl - totalFunding } : {}),
  };
}
